// Reusable soft-delete + Undo flow for any admin list.
// Handles the "click trash → immediate delete → Sonner toast with Undo →
// Ctrl/Cmd+Z also undoes it" pattern used across blogs, pathways, glossary.
//
// Usage:
//   const undoDelete = useUndoDelete();
//   const onClickTrash = () => undoDelete.run({
//     label: "Deleted \"Foo\"",
//     deletePath: `/pathways/${id}`,
//     restorePath: `/pathways/${id}/restore`,
//     onDone: refresh,       // called after delete AND after restore
//   });
import { api } from "@/lib/api";
import { toast } from "sonner";

export function useUndoDelete() {
  const run = async ({ label, deletePath, restorePath, onDone }) => {
    try {
      await api.delete(deletePath);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
      return;
    }
    onDone && onDone();

    let undone = false;
    const doUndo = async () => {
      if (undone) return;
      undone = true;
      try {
        await api.post(restorePath);
        toast.success(`Restored — ${label.replace(/^Deleted\s*/i, "")}`);
        onDone && onDone();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not restore");
      }
    };
    // Ctrl/Cmd+Z anywhere except inside inputs so real text-undo still works.
    const onKey = (e) => {
      if (e.key !== "z" && e.key !== "Z") return;
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.shiftKey) return; // that's redo
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      e.preventDefault();
      doUndo();
      toast.dismiss(toastId);
    };
    window.addEventListener("keydown", onKey);

    const toastId = toast(label, {
      description: "Press Ctrl+Z or tap Undo to restore",
      duration: 8000,
      action: { label: "Undo", onClick: () => doUndo() },
      onDismiss: () => window.removeEventListener("keydown", onKey),
      onAutoClose: () => window.removeEventListener("keydown", onKey),
    });
  };
  return { run };
}
